import React from 'react';
import { Box, TableRow, TableCell, Tooltip, Typography } from '@mui/material';
import { MapPin, ContactRound, Pencil, Trash2 } from 'lucide-react';
import { ProveedorListItem } from '../types';

interface Props {
  proveedor: ProveedorListItem;
  onEditar: (p: ProveedorListItem) => void;
  onEliminar: (p: ProveedorListItem) => void;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]?.toUpperCase() ?? '').join('');
}

export function ProveedorRowCard({ proveedor, onEditar, onEliminar }: Props) {
  return (
    <TableRow
      data-testid="proveedor-row"
      sx={{
        height: 76,
        background: '#FFFFFF',
        border: '1px solid rgba(116, 48, 95, 0.10)',
        borderRadius: '8px',
        boxShadow: '0 3px 7px rgba(45,30,46,0.08), 0 12px 24px rgba(45,30,46,0.045)',
        transition: 'transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease',
        transform: 'translateZ(0)',
        cursor: 'pointer',
        '& td': { borderBottom: 'none' },
        '& td:first-of-type': { borderRadius: '8px 0 0 8px' },
        '& td:last-of-type': { borderRadius: '0 8px 8px 0' },
        '&:hover': {
          transform: 'translateY(-3px) translateZ(0)',
          boxShadow: '0 8px 16px rgba(52,32,54,0.11), 0 18px 32px rgba(52,32,54,0.08)',
          borderColor: 'rgba(168,79,141,0.28)',
          background: '#FFFCFE',
        },
      }}
      onClick={() => onEditar(proveedor)}
    >
      {/* Razón social + NIT */}
      <TableCell sx={{ pl: 0, pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 4, height: 52, borderRadius: '8px 0 0 8px', background: '#7BA66F', flexShrink: 0 }} />
          <Box
            sx={{
              width: 42, height: 42, borderRadius: '8px', background: '#F1E3ED',
              border: '1px solid #E1C9D9', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 750, color: '#74305F', lineHeight: 1 }}>
              {getInitials(proveedor.razonSocial)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              data-testid="proveedor-razon-social"
              sx={{ fontSize: 15, fontWeight: 700, color: '#2C232D', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}
            >
              {proveedor.razonSocial}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#7A727C' }}>
              NIT {proveedor.nit}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Ubicación */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: '#EDF6F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MapPin size={20} color="#4C7B87" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: '#3E353F', lineHeight: 1.2 }}>
              {proveedor.ciudad ?? '—'}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 400, color: '#817984', lineHeight: 1.2 }}>
              {proveedor.departamento ?? ''}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Contactos */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '8px', background: '#F3EDF7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ContactRound size={21} color="#6B4A82" />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#433A45' }}>
            {proveedor.cantidadContactos}
          </Typography>
        </Box>
      </TableCell>

      {/* Acciones */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Tooltip title="Editar proveedor">
            <Box
              component="button"
              aria-label="Editar proveedor"
              data-testid="editar-btn"
              onClick={() => onEditar(proveedor)}
              sx={{
                width: 38, height: 38, borderRadius: '8px', border: 'none', background: '#F8EEF5',
                color: '#87446F', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 5px rgba(45,30,46,0.07)',
                transition: 'background 150ms, transform 150ms',
                '&:hover': { background: '#EEDAE8', transform: 'translateY(-1px)' },
              }}
            >
              <Pencil size={18} />
            </Box>
          </Tooltip>
          <Tooltip title="Eliminar proveedor">
            <Box
              component="button"
              aria-label="Eliminar proveedor"
              data-testid="eliminar-btn"
              onClick={() => onEliminar(proveedor)}
              sx={{
                width: 38, height: 38, borderRadius: '8px', border: 'none', background: '#FCEEEF',
                color: '#B4232C', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 5px rgba(45,30,46,0.07)',
                transition: 'background 150ms, transform 150ms',
                '&:hover': { background: '#F7DADC', transform: 'translateY(-1px)' },
              }}
            >
              <Trash2 size={18} />
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}
